'use client';

import { createContext, ReactNode, useContext } from 'react';
import {
  ShowcaseButton,
  ShowcaseSpotlightContainer,
  ShowcaseStepContent,
  ShowcaseSpotlight,
  findShowcaseTarget,
  DEFAULT_SHOWCASE_STEP_CONTENT_WIDTH,
} from './primitives';
import { useResponsive } from '../../hooks/use-responsive';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '../visually-hidden';

export interface ShowcaseOptions {
  offset: {
    x: number | 'center';
    y: number;
  };
}

export interface ShowcaseStep {
  id: string;
  nextId?: string;
  prevId?: string;
  finishText?: string;
  canSkip?: boolean;
  content?: ReactNode | null;
  targetId?: string;
  highlightTargetIds?: string[];
  contentWidth?: number;
  options?: ShowcaseOptions;
  onLoad?: () => void;

  side?: 'top' | 'bottom' | 'auto';
}

export interface ShowcaseContextValue {
  showCaseId: string;
  currentStep: ShowcaseStep | undefined;
  steps: ShowcaseStep[];
  isTransitioning: boolean;
  canSkip?: boolean;
  options?: ShowcaseOptions;

  start: () => void;
  goNext: () => void;
  goBack: () => void;
  close: () => void;
  finish: () => void;
}

export const ShowcaseContext = createContext<ShowcaseContextValue | null>(null);

export const Showcase = (value: ShowcaseContextValue) => {
  return (
    <ShowcaseContext.Provider value={value}>
      <ShowcaseConsumer />
    </ShowcaseContext.Provider>
  );
};

export const useShowcase = () => {
  const context = useContext(ShowcaseContext);
  if (!context) {
    throw new Error('useShowcase must be used within a ShowcaseProvider');
  }
  return context;
};

const centerX = {
  left: '50%',
  transform: 'translateX(-50%)',
};

const centerXY = {
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

const defaultOptions: ShowcaseOptions = {
  offset: {
    x: 0,
    y: 0,
  },
};

export const ShowcaseStepConsumer = () => {
  const {
    currentStep,
    close,
    finish,
    goBack,
    goNext,
    isTransitioning,
    showCaseId,
    canSkip,
    options: showcaseOptions,
  } = useShowcase();
  const { isMobile } = useResponsive();
  if (!currentStep) return null;

  const getRect = () => {
    if (!currentStep.targetId) return undefined;
    const currentTarget = findShowcaseTarget(currentStep.targetId, showCaseId);
    return currentTarget?.getBoundingClientRect();
  };

  const width = currentStep.contentWidth ?? DEFAULT_SHOWCASE_STEP_CONTENT_WIDTH;

  // Priority: step options > showcase options > default options
  const options = currentStep.options ?? showcaseOptions ?? defaultOptions;

  const getPostitionX = (rect: DOMRect | undefined) => {
    if (!rect) return {};
    if (isMobile) {
      return centerX;
    }

    if (options.offset.x === 'center') {
      return {
        left: rect.left + (rect.width - width) / 2,
      };
    }

    return {
      left: rect.left + (options?.offset.x ?? 0),
    };
  };

  const getPositionY = (rect: DOMRect | undefined) => {
    if (!rect) return {};
    const top = rect.bottom + options.offset.y;
    const bottom = window.innerHeight - rect.top + options.offset.y;

    if (currentStep.side === 'bottom') {
      return {
        top: top,
        bottom: undefined,
      };
    }

    if (currentStep.side === 'top') {
      return {
        top: undefined,
        bottom: bottom,
      };
    }

    const isBelowHalf = bottom * 2 < top;
    return {
      top: isBelowHalf ? undefined : top,
      bottom: isBelowHalf ? bottom : undefined,
    };
  };

  const getPosition = (rect: DOMRect | undefined) => {
    if (!rect) return centerXY;
    return {
      ...getPostitionX(rect),
      ...getPositionY(rect),
    };
  };

  if (isTransitioning || !currentStep.content) {
    return null;
  }

  return (
    <ShowcaseStepContent
      content={currentStep.content}
      onClose={close}
      style={getPosition(getRect())}
      nextButton={
        currentStep?.nextId ? (
          <ShowcaseButton onClick={goNext}>NEXT</ShowcaseButton>
        ) : (
          <ShowcaseButton onClick={finish}>
            {currentStep.finishText ?? 'Finish'}
          </ShowcaseButton>
        )
      }
      backButton={
        <div className="flex items-center justify-start gap-2">
          {currentStep?.prevId && (
            <ShowcaseButton onClick={goBack}>BACK</ShowcaseButton>
          )}
          {canSkip && currentStep?.nextId && (
            <ShowcaseButton onClick={finish}>SKIP</ShowcaseButton>
          )}
        </div>
      }
    />
  );
};

export const ShowcaseSpotlightConsumer = () => {
  const { currentStep, isTransitioning, showCaseId } = useShowcase();
  return (
    <ShowcaseSpotlightContainer>
      {currentStep?.targetId && !isTransitioning && (
        <ShowcaseSpotlight
          key={currentStep.id}
          targetId={currentStep.targetId}
          showCaseId={showCaseId}
        />
      )}
      {currentStep?.highlightTargetIds &&
        !isTransitioning &&
        currentStep.highlightTargetIds.map((item) => (
          <ShowcaseSpotlight
            key={item}
            targetId={item}
            showCaseId={showCaseId}
          />
        ))}
    </ShowcaseSpotlightContainer>
  );
};

export const ShowcaseConsumer = () => {
  const { currentStep, goNext } = useShowcase();
  return (
    <DialogPrimitive.Root open={currentStep != null}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay />
        <DialogPrimitive.Content
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            e.stopPropagation();
            e.preventDefault();
            goNext();
          }}
        >
          <VisuallyHidden>
            <DialogPrimitive.Title>Tutorial</DialogPrimitive.Title>
          </VisuallyHidden>
          <ShowcaseStepConsumer />
          <ShowcaseSpotlightConsumer />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
